import React, { useEffect, useRef, useState } from "react";
import { Button } from "@mui/material";
import { toast } from "react-toastify";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import API from "../../../api/axios";
import { APP_URLS } from "../../../api/url";
import { formatDate, humanize, toastError } from "../helpers";
import { LoadingRow } from "./Shared";

const LANGUAGES = [
  { name: "English", value: "en" },
  { name: "Français", value: "fr" },
];

/**
 * The docs are written in GitHub-flavoured Markdown (tables of fields and error codes, JSON and
 * curl blocks), so remark-gfm is required — without it the tables arrive as rows of pipes.
 * react-markdown does not render raw HTML unless rehype-raw is added, which is why no sanitiser
 * is needed here; keep it that way. Wide tables scroll rather than squeeze the page.
 */
const MARKDOWN_COMPONENTS = {
  table: ({ node, ...props }) => (
    <div className="table-scroll">
      <table {...props} />
    </div>
  ),
  a: ({ node, children, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
};

const Markdown = ({ children }) => (
  <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
    {children}
  </ReactMarkdown>
);

/**
 * What approved dealers read (GET /documentation?lang=): support staff see exactly the same
 * sections, rendered the same way. The OpenAPI document can be downloaded as JSON.
 */
const DocsTab = () => {
  const [lang, _lang] = useState("en");
  const [doc, _doc] = useState(null);
  const [loading, _loading] = useState(true);
  const seq = useRef(0);

  useEffect(() => {
    const current = ++seq.current;
    _loading(true);
    API.get(`${APP_URLS.DEALER_API}/documentation?lang=${lang}`)
      .then((res) => {
        if (current !== seq.current) return;
        if (res.data.success === true) {
          _doc(res.data.data);
        } else {
          toast.error(res.data.message, { position: toast.POSITION.TOP_RIGHT });
        }
      })
      .catch((error) => current === seq.current && toastError(error))
      .finally(() => current === seq.current && _loading(false));
  }, [lang]);

  const downloadOpenApi = () => {
    const blob = new Blob([JSON.stringify(doc.openapi, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `cartobike-dealer-api-${doc.api_version}.openapi.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="filters">
        <select className="filter-select" value={lang} onChange={(e) => _lang(e.target.value)}>
          {LANGUAGES.map((l) => (
            <option key={l.value} value={l.value}>
              {l.name}
            </option>
          ))}
        </select>
        {doc?.openapi && !loading && (
          <Button variant="outlined" onClick={downloadOpenApi} sx={{ textTransform: "none" }}>
            Download OpenAPI
          </Button>
        )}
      </div>

      {loading ? (
        <table className="auctionTable">
          <LoadingRow colSpan={1} />
        </table>
      ) : doc ? (
        <>
          <p className="text-start muted">
            {doc.api_version} · base URL <span className="mono">{doc.base_urls?.current}</span> · header{" "}
            <span className="mono">{doc.auth_header}</span> · served in {doc.language}
            {doc.language !== lang ? ` (no ${lang} translation, English shown)` : ""} · updated {formatDate(doc.updated_at)}
          </p>

          {doc.limits && (
            <p className="text-start">
              {Object.entries(doc.limits).map(([name, value]) => (
                <span key={name} className="me-3">
                  {humanize(name)}: <b>{value}</b>
                </span>
              ))}
            </p>
          )}

          {(doc.sections ?? []).map((section) => (
            <div key={section.id} className="doc-section">
              <h3>{section.title}</h3>
              <div className="doc-body">
                <Markdown>{section.body_markdown}</Markdown>
              </div>
            </div>
          ))}
        </>
      ) : null}
    </>
  );
};

export default DocsTab;
